/**
 * Portal mirror of the orchestrator's FileClassification (#3996).
 *
 * The wire value is untrusted JSON from the model. Invalid entries are
 * dropped and arrays are bounded to the contract. Money stays integer cents:
 * a fractional `unitPriceCents` is discarded, never rounded here.
 */

export const FILE_CLASSIFICATION_KINDS = [
  'inspection_certificate',
  'parts_list',
  'vehicle_photo',
  'engine_photo',
  'damage_photo',
  'fault_codes',
  'registration_document',
  'invoice_or_receipt',
  'other',
] as const;

export type FileClassificationKind = (typeof FILE_CLASSIFICATION_KINDS)[number];

export const FILE_CLASSIFICATION_ACTIONS = [
  'attach_to_vehicle',
  'attach_to_work_order',
  'add_work_order_note',
  'create_work_order',
  'add_work_order_items',
  'import_parts',
] as const;

export type FileClassificationAction = (typeof FILE_CLASSIFICATION_ACTIONS)[number];

export const FILE_CLASSIFICATION_MISSING = [
  'target',
  'kind',
  'registration_number',
  'date',
  'lines',
] as const;

export type FileClassificationMissing = (typeof FILE_CLASSIFICATION_MISSING)[number];

/** Below this confidence the card shows the uncertain chip (#3996). */
export const FILE_CLASSIFICATION_UNCERTAIN_BELOW = 0.6;

const MAX_CLASSIFICATIONS = 5;
const MAX_LINES = 200;
const MAX_FAULT_CODES = 100;
const MAX_OBSERVATIONS = 50;
const MAX_SUMMARY = 500;
const MAX_OBSERVATION = 300;
const SCAN_CAP = 1000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INSPECTION_RESULTS = ['passed', 'failed', 'reinspection'] as const;
type InspectionResult = (typeof INSPECTION_RESULTS)[number];

const TARGET_ENTITIES = ['vehicle', 'booking'] as const;
type TargetEntity = (typeof TARGET_ENTITIES)[number];

const TARGET_SOURCES = ['page_context', 'file', 'user'] as const;
type TargetSource = (typeof TARGET_SOURCES)[number];

export interface FileClassificationLine {
  name: string;
  partNumber?: string;
  quantity?: number;
  unit?: string;
  unitPriceCents?: number;
}

export interface FileClassificationFaultCode {
  code: string;
  description?: string;
}

export interface FileClassificationTarget {
  entityType: TargetEntity | null;
  entityId: string | null;
  label: string | null;
  source: TargetSource | null;
}

export interface FileClassificationFields {
  registrationNumber?: string;
  vin?: string;
  date?: string;
  inspectionResult?: InspectionResult;
  lines?: FileClassificationLine[];
  faultCodes?: FileClassificationFaultCode[];
  observations?: string[];
}

export interface FileClassification {
  attachmentId: string;
  kind: FileClassificationKind;
  confidence: number;
  summary: string;
  fields: FileClassificationFields;
  target: FileClassificationTarget;
  missing: FileClassificationMissing[];
  suggestedActions: FileClassificationAction[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

export function isFileClassificationKind(value: unknown): value is FileClassificationKind {
  return isOneOf(value, FILE_CLASSIFICATION_KINDS);
}

function boundedString(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

function takeValid<T>(value: unknown, max: number, parse: (item: unknown) => T | undefined): T[] {
  if (!Array.isArray(value)) return [];
  const out: T[] = [];
  const scan = Math.min(value.length, SCAN_CAP);
  for (let index = 0; index < scan && out.length < max; index += 1) {
    const parsed = parse(value[index]);
    if (parsed !== undefined) out.push(parsed);
  }
  return out;
}

function parseEnumList<T extends string>(value: unknown, allowed: readonly T[], max: number): T[] {
  const seen = new Set<T>();
  return takeValid(value, max, (item) => {
    if (!isOneOf(item, allowed) || seen.has(item)) return undefined;
    seen.add(item);
    return item;
  });
}

function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function isCents(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

function parseLine(value: unknown): FileClassificationLine | undefined {
  if (!isRecord(value)) return undefined;
  const name = boundedString(value.name, 200);
  if (!name) return undefined;
  const line: FileClassificationLine = { name };
  const partNumber = boundedString(value.partNumber, 64);
  if (partNumber) line.partNumber = partNumber;
  if (
    typeof value.quantity === 'number' &&
    Number.isFinite(value.quantity) &&
    value.quantity >= 0 &&
    value.quantity <= 1_000_000
  ) {
    line.quantity = value.quantity;
  }
  const unit = boundedString(value.unit, 32);
  if (unit) line.unit = unit;
  if (isCents(value.unitPriceCents)) line.unitPriceCents = value.unitPriceCents;
  return line;
}

function parseFaultCode(value: unknown): FileClassificationFaultCode | undefined {
  if (!isRecord(value)) return undefined;
  const code = boundedString(value.code, 32);
  if (!code) return undefined;
  const fault: FileClassificationFaultCode = { code };
  const description = boundedString(value.description, 300);
  if (description) fault.description = description;
  return fault;
}

function parseObservation(value: unknown): string | undefined {
  return boundedString(value, MAX_OBSERVATION);
}

function parseFields(value: unknown): FileClassificationFields {
  const fields: FileClassificationFields = {};
  if (!isRecord(value)) return fields;
  const registrationNumber = boundedString(value.registrationNumber, 32);
  if (registrationNumber) fields.registrationNumber = registrationNumber;
  const vin = boundedString(value.vin, 32);
  if (vin) fields.vin = vin;
  if (isIsoDate(value.date)) fields.date = value.date;
  if (isOneOf(value.inspectionResult, INSPECTION_RESULTS))
    fields.inspectionResult = value.inspectionResult;
  const lines = takeValid(value.lines, MAX_LINES, parseLine);
  if (lines.length > 0) fields.lines = lines;
  const faultCodes = takeValid(value.faultCodes, MAX_FAULT_CODES, parseFaultCode);
  if (faultCodes.length > 0) fields.faultCodes = faultCodes;
  const observations = takeValid(value.observations, MAX_OBSERVATIONS, parseObservation);
  if (observations.length > 0) fields.observations = observations;
  return fields;
}

function parseTarget(value: unknown): FileClassificationTarget {
  const empty: FileClassificationTarget = {
    entityType: null,
    entityId: null,
    label: null,
    source: null,
  };
  if (!isRecord(value)) return empty;
  const entityType = isOneOf(value.entityType, TARGET_ENTITIES) ? value.entityType : null;
  const entityId = boundedString(value.entityId, 64);
  return {
    entityType,
    entityId: entityType && entityId ? entityId : null,
    label: boundedString(value.label, 200) ?? null,
    source: isOneOf(value.source, TARGET_SOURCES) ? value.source : null,
  };
}

function parseSummary(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > MAX_SUMMARY ? trimmed.slice(0, MAX_SUMMARY) : trimmed;
}

function parseConfidence(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1)
    return undefined;
  return value;
}

function parseAttachmentId(value: unknown): string | undefined {
  if (typeof value !== 'string' || !UUID.test(value)) return undefined;
  return value;
}

function parseOne(value: unknown): FileClassification | undefined {
  if (!isRecord(value)) return undefined;
  const attachmentId = parseAttachmentId(value.attachmentId);
  const kind = isFileClassificationKind(value.kind) ? value.kind : undefined;
  const confidence = parseConfidence(value.confidence);
  const summary = parseSummary(value.summary);
  if (!attachmentId || !kind || confidence === undefined || summary === undefined) return undefined;
  return {
    attachmentId,
    kind,
    confidence,
    summary,
    fields: parseFields(value.fields),
    target: parseTarget(value.target),
    missing: parseEnumList(
      value.missing,
      FILE_CLASSIFICATION_MISSING,
      FILE_CLASSIFICATION_MISSING.length
    ),
    suggestedActions: parseEnumList(
      value.suggestedActions,
      FILE_CLASSIFICATION_ACTIONS,
      FILE_CLASSIFICATION_ACTIONS.length
    ),
  };
}

/** Drop invalid entries. At most five classifications, first valid occurrence of each attachment wins. */
export function parseFileClassifications(value: unknown): FileClassification[] {
  const seen = new Set<string>();
  return takeValid(value, MAX_CLASSIFICATIONS, (item) => {
    const parsed = parseOne(item);
    if (!parsed || seen.has(parsed.attachmentId)) return undefined;
    seen.add(parsed.attachmentId);
    return parsed;
  });
}

/**
 * Spread onto an assistant message. Absent when nothing valid remains, so a
 * later upsert for the same id cannot wipe a classification with `undefined`.
 */
export function presentFileClassifications(
  value: unknown
): { fileClassifications: FileClassification[] } | undefined {
  const fileClassifications = parseFileClassifications(value);
  return fileClassifications.length > 0 ? { fileClassifications } : undefined;
}
